import { ApolloError, useQuery } from '@apollo/client'
import gql from 'graphql-tag'
import { useMemo } from 'react'

import { UserTradesQuery } from './__generated__/types-and-hooks'
import { apolloCompetitionClient } from './apollo'

const query = gql`
  query UserTrades($user: String!, $day: Int!) {
    trades(first: 1000, where: { day_in: [0, $day], user: $user }) {
      id
      token {
        symbol
        name
        id
        multiplierEnd
        multiplierStart
        multiplier
      }
      day
      clearTokenVolume
      clearUsdVolume
      bonusUsdVolume
      txCount
    }
  }
`

const getCurrentDay = () => {
  return Math.floor(Date.now() / 1000 / 86400)
}

export type UserTradeT = UserTradesQuery['trades'][number] & { totalUsdVolume: number; atv: number }
interface UserTradeFormatted {
  volume24: number
  score24: number
  dayTrade: { [address: string]: UserTradeT }
  totalTrade: UserTradeT[]
}
export interface IUserTrade {
  error?: ApolloError
  isLoading: boolean
  data: UserTradeFormatted
}
export default function useUserTrades(account?: string): IUserTrade {
  const {
    data,
    loading: isLoading,
    error,
  } = useQuery<UserTradesQuery>(query, {
    variables: {
      user: account?.toLowerCase(),
      day: getCurrentDay(),
    },
    client: apolloCompetitionClient,
    pollInterval: 10000,
  })

  return useMemo(() => {
    let volume24 = 0
    let score24 = 0
    if (error || isLoading || !data?.trades) {
      return {
        error,
        isLoading,
        data: {
          dayTrade: {},
          totalTrade: [],
          volume24,
          score24,
        },
      }
    }

    const { dayTrade, totalTrade } = data.trades.reduce(
      (prev, curr) => {
        const clear = Number(curr.clearUsdVolume)
        const bonus = Number(curr.bonusUsdVolume)
        const atv = clear / Number(curr.txCount)
        const extendedCurr: UserTradeT = { ...curr, totalUsdVolume: bonus + clear, atv }
        if (extendedCurr.day !== 0) {
          volume24 = volume24 + clear
          score24 = score24 + bonus + clear
          return { ...prev, dayTrade: { ...prev.dayTrade, [extendedCurr.token.id]: extendedCurr } }
        }
        return { ...prev, totalTrade: [...prev.totalTrade, extendedCurr] }
      },
      {
        dayTrade: {} as { [address: string]: UserTradeT },
        totalTrade: [] as UserTradeT[],
      }
    )

    return {
      error,
      isLoading,
      data: {
        dayTrade,
        totalTrade,
        volume24,
        score24,
      },
    }
  }, [data, error, isLoading])
}
